/**
 * WasteAssociationProblemsPanel - Lista de problemas de asociaciones Waste
 *
 * @remarks
 * MVP: Muestra lista de entidades con problemas, permite:
 * - Ver estado (OK/REVISAR/ERROR)
 * - Click para centrar mapa en entidad
 * - Acciones: Confirmar / Ignorar
 * - Feedback completo: modal de confirmación + toasts de éxito/error
 *
 * @see WASTE_ASSOCIATIONS_EXPERIENCE.md
 * @see docs/WASTE_ASSOCIATION_FEEDBACK_SYSTEM.md
 */

import { useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Check, X, Loader2, Eye, EyeOff, Lock } from 'lucide-react';
import {
  useWasteAssociationProblems,
  getStateColor,
  confirmAssociation,
  ignoreAssociation,
  type SimpleAssociationState,
} from '../../../spatial/hooks/useWasteAssociationAnalysis';
import type { AssociationProblem } from '../../../spatial/services/point-association-api.service';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { SuccessToast } from '../../../components/SuccessToast';
import { ErrorToast } from '../../../projects/components/ErrorToast';

/**
 * Tipos de acción pendiente de confirmación
 */
type PendingAction = {
  type: 'confirm' | 'ignore' | 'unlock';
  problem: AssociationProblem;
};

interface WasteAssociationProblemsPanelProps {
  projectId: string | null;
  onSelectEntity?: (entityType: string, entityId: string, coordinates: [number, number]) => void;
  onRefreshNeeded?: () => void;
  /** Whether the map layer is visible */
  layerVisible?: boolean;
  /** Callback to toggle layer visibility */
  onToggleLayer?: (visible: boolean) => void;
}

/**
 * Icon for state
 */
function StateIcon({ state }: { state: SimpleAssociationState }) {
  const color = getStateColor(state);
  switch (state) {
    case 'OK':
      return <CheckCircle size={16} style={{ color }} />;
    case 'REVISAR':
      return <AlertTriangle size={16} style={{ color }} />;
    case 'ERROR':
      return <XCircle size={16} style={{ color }} />;
  }
}

/**
 * Label for entity type
 */
function getEntityTypeLabel(entityType: string): string {
  switch (entityType) {
    case 'waste_container':
      return 'Contenedor';
    case 'waste_depot':
      return 'Depósito';
    case 'waste_hotspot':
      return 'Microbasural';
    default:
      return entityType;
  }
}

/**
 * Problem item component
 */
function ProblemItem({
  problem,
  onSelect,
  onConfirm,
  onIgnore,
  isProcessing,
  isLocked,
}: {
  problem: AssociationProblem;
  onSelect: () => void;
  onConfirm: () => void;
  onIgnore: () => void;
  isProcessing: boolean;
  isLocked?: boolean;
}) {
  const hint = STATE_HINTS[problem.state];

  return (
    <div
      className="flex items-center gap-2 p-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
      onClick={onSelect}
    >
      <StateIcon state={problem.state} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-sm font-medium truncate">
          {problem.code || problem.entityId.slice(0, 8)}
          {isLocked && (
            <span title="Asociación confirmada manualmente">
              <Lock size={12} className="text-amber-500 flex-shrink-0" />
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {getEntityTypeLabel(problem.entityType)}
          {problem.distance !== null && ` • ${problem.distance.toFixed(1)}m`}
        </div>
        {/* Hint sutil para estados dudosos */}
        {hint && (
          <div className="text-xs text-amber-600 italic mt-0.5">
            {hint}
          </div>
        )}
      </div>
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {/* Confirmar: verde */}
        <button
          className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
          onClick={onConfirm}
          disabled={isProcessing}
          title="Confirmar asociación"
        >
          {isProcessing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        </button>
        {/* Ignorar: gris/neutro (no verde) */}
        <button
          className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-50"
          onClick={onIgnore}
          disabled={isProcessing}
          title="Ignorar problema"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Mensajes de feedback según catálogo
 * @see docs/WASTE_ASSOCIATION_FEEDBACK_SYSTEM.md
 */
const FEEDBACK_MESSAGES = {
  confirm: {
    modalTitle: '¿Confirmar ubicación?',
    modalMessage: 'Esta asociación quedará bloqueada y no se recalculará automáticamente si movés la entidad.',
    confirmText: 'Confirmar',
    success: 'Ubicación confirmada',
    error: 'No se pudo confirmar la ubicación',
    variant: 'warning' as const, // Amarillo - acción con consecuencias
  },
  ignore: {
    modalTitle: '¿Ignorar este problema?',
    modalMessage: 'El sistema dejará de marcar este problema automáticamente. Podés revertirlo desbloqueando la asociación.',
    confirmText: 'Ignorar',
    success: 'Problema ignorado',
    error: 'No se pudo ignorar el problema',
    variant: 'info' as const, // Gris/neutro - no es destructivo
  },
  unlock: {
    modalTitle: '¿Desbloquear asociación?',
    modalMessage: 'La asociación se recalculará automáticamente si cambia la ubicación de la entidad.',
    confirmText: 'Desbloquear',
    success: 'Asociación desbloqueada',
    error: 'No se pudo desbloquear la asociación',
    variant: 'info' as const,
  },
  move: {
    success: 'Asociación actualizada',
    error: 'No se pudo recalcular la asociación',
  },
} as const;

/**
 * Hint para estados dudosos
 */
const STATE_HINTS: Record<string, string | undefined> = {
  REVISAR: 'Este punto podría estar mal ubicado',
  ERROR: 'Este punto no tiene asociación válida',
};

/**
 * Main component
 */
export function WasteAssociationProblemsPanel({
  projectId,
  onSelectEntity,
  onRefreshNeeded,
  layerVisible = true,
  onToggleLayer,
}: WasteAssociationProblemsPanelProps) {
  const { data, isLoading, error, refetch } = useWasteAssociationProblems(projectId);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'revisar' | 'error'>('all');

  // Estado para modal de confirmación
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // Estado para toasts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Solicita confirmación antes de ejecutar la acción
   */
  const requestConfirm = useCallback((problem: AssociationProblem) => {
    setPendingAction({ type: 'confirm', problem });
  }, []);

  /**
   * Solicita confirmación antes de ignorar
   */
  const requestIgnore = useCallback((problem: AssociationProblem) => {
    setPendingAction({ type: 'ignore', problem });
  }, []);

  /**
   * Cierra el modal sin ejecutar
   */
  const closeModal = useCallback(() => {
    setPendingAction(null);
    setIsModalLoading(false);
  }, []);

  /**
   * Ejecuta la acción pendiente después de confirmar en el modal
   */
  const executeAction = useCallback(async () => {
    if (!pendingAction) return;

    const { type, problem } = pendingAction;
    const messages = FEEDBACK_MESSAGES[type];

    setIsModalLoading(true);
    setProcessingId(problem.entityId);

    try {
      // Ejecutar acción según tipo
      switch (type) {
        case 'confirm':
          await confirmAssociation(problem.entityType, problem.entityId);
          break;
        case 'ignore':
          await ignoreAssociation(problem.entityType, problem.entityId);
          break;
        case 'unlock':
          // TODO: Implementar unlockAssociation cuando se agregue al hook
          console.warn('[WasteAssociationProblemsPanel] unlock not implemented yet');
          break;
      }

      // Cerrar modal y mostrar éxito
      setPendingAction(null);
      setIsModalLoading(false);
      setSuccessMessage(messages.success);

      // Refrescar datos
      await refetch();
      onRefreshNeeded?.();
    } catch (err) {
      console.error(`Error ${type}:`, err);
      setErrorMessage(messages.error);
    } finally {
      setIsModalLoading(false);
      setProcessingId(null);
    }
  }, [pendingAction, refetch, onRefreshNeeded]);

  const handleSelect = (problem: AssociationProblem) => {
    if (problem.geometry?.coordinates) {
      onSelectEntity?.(
        problem.entityType,
        problem.entityId,
        problem.geometry.coordinates as [number, number]
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span className="text-sm text-gray-500">Cargando problemas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500 text-sm">
        Error: {error.message}
      </div>
    );
  }

  if (!data || data.summary.total === 0) {
    return (
      <div className="p-4 text-center text-green-600">
        <CheckCircle className="mx-auto mb-2" size={32} />
        <div className="text-sm font-medium">Sin problemas</div>
        <div className="text-xs text-gray-500">Todas las asociaciones están correctas</div>
      </div>
    );
  }

  // Filter problems by tab
  const filteredProblems = (() => {
    switch (activeTab) {
      case 'revisar':
        return data.byType.distance_warning;
      case 'error':
        return [...data.byType.distance_error, ...data.byType.no_association];
      default:
        return [
          ...data.byType.no_association,
          ...data.byType.distance_error,
          ...data.byType.distance_warning,
        ];
    }
  })();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={18} className="text-amber-500" />
          <span className="font-medium text-sm">Problemas de Asociación</span>
          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
            {data.summary.total}
          </span>
          {/* Toggle para mostrar/ocultar capa en mapa */}
          <button
            className={`ml-auto p-1.5 rounded transition-colors ${
              layerVisible
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
            }`}
            onClick={() => onToggleLayer?.(!layerVisible)}
            title={layerVisible ? 'Ocultar estados en mapa' : 'Mostrar estados en mapa'}
          >
            {layerVisible ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          <button
            className={`px-2 py-1 text-xs rounded ${
              activeTab === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-200'
            }`}
            onClick={() => setActiveTab('all')}
          >
            Todos ({data.summary.total})
          </button>
          <button
            className={`px-2 py-1 text-xs rounded ${
              activeTab === 'revisar' ? 'bg-amber-500 text-white' : 'bg-gray-200'
            }`}
            onClick={() => setActiveTab('revisar')}
          >
            Revisar ({data.summary.byState.REVISAR || 0})
          </button>
          <button
            className={`px-2 py-1 text-xs rounded ${
              activeTab === 'error' ? 'bg-red-500 text-white' : 'bg-gray-200'
            }`}
            onClick={() => setActiveTab('error')}
          >
            Error ({data.summary.byState.ERROR || 0})
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredProblems.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No hay problemas en esta categoría
          </div>
        ) : (
          filteredProblems.map((problem) => (
            <ProblemItem
              key={`${problem.entityType}:${problem.entityId}`}
              problem={problem}
              onSelect={() => handleSelect(problem)}
              onConfirm={() => requestConfirm(problem)}
              onIgnore={() => requestIgnore(problem)}
              isProcessing={processingId === problem.entityId}
              isLocked={problem.isLocked}
            />
          ))
        )}
      </div>

      {/* Footer - Summary by entity type */}
      <div className="p-2 border-t border-gray-200 bg-gray-50">
        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          {Object.entries(data.summary.byEntityType).map(([type, count]) => (
            <span key={type} className="bg-gray-200 px-2 py-0.5 rounded">
              {getEntityTypeLabel(type)}: {count}
            </span>
          ))}
        </div>
      </div>

      {/* Modal de confirmación */}
      {pendingAction && (
        <ConfirmModal
          isOpen={true}
          onClose={closeModal}
          onConfirm={executeAction}
          title={FEEDBACK_MESSAGES[pendingAction.type].modalTitle}
          message={FEEDBACK_MESSAGES[pendingAction.type].modalMessage}
          confirmText={FEEDBACK_MESSAGES[pendingAction.type].confirmText}
          cancelText="Cancelar"
          variant={FEEDBACK_MESSAGES[pendingAction.type].variant}
          isLoading={isModalLoading}
        />
      )}

      {/* Toast de éxito */}
      <SuccessToast
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
      />

      {/* Toast de error */}
      <ErrorToast
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    </div>
  );
}

export default WasteAssociationProblemsPanel;
